import { Chart, ChartEvent, ChartOptions, ClickOnTickLabelClick } from 'chart.js';
import {
  commonOptions,
  compLabels,
  displayDataLabels,
  displayScaleInMinutes,
} from '../../utilities/commonOptions';
import { cloneDeep, merge } from 'lodash';
import { DataSet } from './types';
import { barChartOptions } from '../../utilities/chartUtils';
import { makeTooltipStyles } from '../ChartTooltip/ChartTooltip';
import { CompareValues } from '../MultiFilter/MultiFilters.types';
import { getEntityName } from '../../utilities/helpers';
import { DisplayOptionValue } from '../../page/genesis-analytics/useControls';

export const makeBarOptions = ({
  chart,
  compareBy,
  tooltipRef,
  allFilters,
  unit = 'minutes',
  showDataLabels,
  extendOptions,
  isUtilizationChart = false,
  displayOptionList,
}: {
  chart: { data: DataSet[] };
  onDataPointClick?: (point: ChartDataPoint, chart: Chart, evt: ChartEvent) => void;
  compareBy: CompareValues;
  tooltipRef: React.MutableRefObject<HTMLDivElement>;
  allFilters: AllFilters;
  showDataLabels?: boolean;
  unit?: string;
  extendOptions?: ChartOptions<'bar'>;
  isUtilizationChart?: boolean;
  displayOptionList?: DisplayOptionValue[];
}) => {
  const l = chart.data.map((d) => d.data).reduce((a, b) => b.concat(a), []);
  let maxValue = Math.max(...l);

  // When data points are shown, account for their values in the max calculation
  // to prevent top data points from being cut off
  if (displayOptionList?.includes('dataPoints')) {
    const dataPointValues = chart.data
      .flatMap((d) => d.dataPoints)
      .flat()
      .map((p) => p?.value || 0);
    if (dataPointValues.length > 0) {
      const maxDataPointValue = Math.max(...dataPointValues);
      maxValue = Math.max(maxValue, maxDataPointValue);
    }
  }

  const options = cloneDeep({ ...barChartOptions, ...commonOptions });

  const groupCount = new window.Set(chart.data.map((t) => t.label.split('-')?.[1])).size;

  return merge(
    merge(
      options,
      {
        scales: {
          y: {
            ticks: {
              stepSize: maxValue < 60 ? 15 : 30,
              callback: displayScaleInMinutes,
            },
            ...(maxValue ? { suggestedMax: maxValue + maxValue / 80 } : {}),
          },
          x: {
            type: 'category',
            ticks: {
              autoSkip: true,
            },
          },
        },
        plugins: {
          tooltip: {
            mode: 'nearest',
            callbacks: {
              label() {
                return '';
              },
              beforeBody(i) {
                const dataIndex = i[0].dataIndex;
                const datasetIndex = i[0].datasetIndex;
                // // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const general = (i[0].dataset as any)?.general;

                const l = Object.values(general)?.length || 0;

                const vIndex = datasetIndex % l;

                const set = Object.values(general)?.[vIndex] as any;

                const metricLabel = compLabels[compareBy as keyof typeof compLabels];

                const useDash = compareBy === 'national_statistics';

                const isAfterHoursModality =
                  metricLabel == 'Modality' && set?.name?.includes('after_hours');

                return [
                  {
                    label: isAfterHoursModality ? 'After Hours' : metricLabel,
                    value: /rest/i.test(set?.name)
                      ? 'Rest'
                      : isAfterHoursModality
                      ? '🕗'
                      : getEntityName({
                          value: set?.name,
                          allFilters,
                          compareBy,
                          isUtilizationChart,
                        }),
                    isHead: true,
                  },
                  ...(set?.maxs.length > 0
                    ? [
                        {
                          label: 'Maximum',
                          value:
                            useDash && !set?.maxs?.[dataIndex]
                              ? '—'
                              : (set?.maxs?.[dataIndex] || 0).toFixed(1) + ' ' + unit,
                        },
                      ]
                    : []),
                  {
                    label: 'Mean',
                    value:
                      useDash && !set?.data?.[dataIndex]
                        ? '—'
                        : (set?.data?.[dataIndex] || 0).toFixed(1) + ' ' + unit,
                  },
                  ...(set?.mins.length > 0
                    ? [
                        {
                          label: 'Minimum',
                          value:
                            useDash && !set?.mins?.[dataIndex]
                              ? '—'
                              : (set?.mins?.[dataIndex] || 0).toFixed(1) + ' ' + unit,
                        },
                      ]
                    : []),
                  {
                    label: 'Standard Deviation',
                    value:
                      useDash && !set?.standardDeviation?.[dataIndex]
                        ? '—'
                        : (set?.standardDeviation?.[dataIndex] || 0).toFixed(1) + ' ' + unit,
                  },
                  {
                    label: 'Sample Size',
                    value:
                      useDash && set?.name !== 'this hospital' && !set?.datalabels?.[dataIndex]
                        ? '—'
                        : Math.floor(set?.datalabels?.[dataIndex] || 0) +
                          (isUtilizationChart ? ' days' : ' cases'),
                  },
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                ] as any;
              },
            },
            enabled: false,
            external: makeTooltipStyles({
              tooltipRef,
              isCalendarIcon: false,
            }),
          },
          datalabels: {
            display: displayDataLabels(!!showDataLabels, isUtilizationChart, groupCount),
          },
        },
      } as ChartOptions<'bar'>,
      extendOptions,
    ),
  ) as ChartOptions<'bar'>;
};
